import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { whatsAppConfigSchema, type WhatsAppConfigFormData } from '@/schemas/whatsapp-config.schema';
import type { WhatsAppConfig } from '@/types';
import { useToast } from '@/hooks/use-toast';

interface WhatsAppConfigFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  mode: 'create' | 'edit';
  initialData?: WhatsAppConfig;
  onSubmit: (data: WhatsAppConfigFormData) => void;
  isSubmitting?: boolean;
}

export function WhatsAppConfigForm({
  open,
  onOpenChange,
  mode,
  initialData,
  onSubmit,
  isSubmitting = false,
}: WhatsAppConfigFormProps) {
  const { toast } = useToast();

  const form = useForm<WhatsAppConfigFormData>({
    resolver: zodResolver(whatsAppConfigSchema),
    defaultValues: {
      serviceId: '',
      verifyToken: '',
      accessToken: '',
      phoneNumberId: '',
      apiUrl: '',
    },
  });

  useEffect(() => {
    if (open && mode === 'edit' && initialData) {
      form.reset({
        serviceId: initialData.serviceId,
        verifyToken: initialData.verifyToken,
        accessToken: initialData.accessToken,
        phoneNumberId: initialData.phoneNumberId,
        apiUrl: initialData.apiUrl,
      });
    } else if (open && mode === 'create') {
      form.reset({
        serviceId: '',
        verifyToken: '',
        accessToken: '',
        phoneNumberId: '',
        apiUrl: '',
      });
    }
  }, [open, mode, initialData, form]);

  const handleSubmit = (data: WhatsAppConfigFormData) => {
    onSubmit(data);
    toast({
      title: mode === 'create' ? 'Configuration created' : 'Configuration updated',
      description: `WhatsApp config for "${data.serviceId}" was saved successfully.`,
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {mode === 'create' ? 'Create WhatsApp Configuration' : 'Edit WhatsApp Configuration'}
          </DialogTitle>
          <DialogDescription>
            {mode === 'create'
              ? 'Add a new WhatsApp Business API configuration.'
              : 'Update the existing WhatsApp configuration.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="serviceId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service ID</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. my-whatsapp-service" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="verifyToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Verify Token</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="Webhook verify token" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="accessToken"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Access Token</FormLabel>
                  <FormControl>
                    <Input type="password" placeholder="WhatsApp API access token" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="phoneNumberId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone Number ID</FormLabel>
                  <FormControl>
                    <Input placeholder="e.g. 123456789" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="apiUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>API URL</FormLabel>
                  <FormControl>
                    <Input placeholder="https://graph.facebook.com/v18.0" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
